import React from "react";
import { capitalize } from "lodash";
import { Box, Typography } from "@mui/material";

import BillDraftItems from "./BillDraftItems";
import Panel from "../../../../components/Panel";
import { tl } from "../../../../components/translate";
import { rupeeDisplay } from "../../../../helpers/rupee";
import { scaleFont } from "../../PrintBill/BillPrintHelpers";
import { getFullName, separateByComma } from "../../../../helpers/formatters";
import {
  BillDocumentType,
  DiscountTypes,
  DocumentTypes
} from "../../../../interfaces/BillInterfaces";
import BilllEditorActions from "../BilllEditorActions";
import { TQrPaymentInfo } from "../../../../interfaces/QrPayment";

interface QrPaymentPanelProps {
  draft: BillDocumentType;
  onClose: () => void;
  currentBillCounter: string;
  title: string;
  children: React.ReactNode;
  selectedPaymentMethod: keyof TQrPaymentInfo | null;
}

export default function QrPanelLayout({
  draft,
  onClose,
  title,
  currentBillCounter,
  children,
  selectedPaymentMethod
}: QrPaymentPanelProps): React.ReactNode {
  const isProductSelected = () =>
    draft?.billItems?.some((item) => item.productId && item.description?.trim());

  return (
    <Panel
      onClose={onClose}
      title={title}
      footer={
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            justifyContent: "flex-end"
          }}
        >
          <BilllEditorActions
            draft={draft}
            isUsedInQrPanel
            isCreditNote={draft.type === DocumentTypes.CREDITNOTE}
            currentBillCounter={currentBillCounter}
          />
        </Box>
      }
    >
      <Box
        width="100%"
        style={{ display: "flex", flexDirection: "column", gap: "20px", overflowY: "auto" }}
      >
        <Box display="flex" alignItems="center">
          <Typography fontWeight={600} width="25%">
            {tl("qrPayment.billNumber")}
          </Typography>
          <Typography>{draft?.billNumber}</Typography>
        </Box>

        {/* client info */}
        <Box display="flex" flexDirection="column">
          <Typography fontWeight={800}>{tl("qrPayment.clientInformation")}</Typography>

          <Box display="flex" alignItems="center">
            <Typography fontWeight={600} width="25%">
              {tl("qrPayment.clientName")}
            </Typography>
            <Typography>{getFullName(draft?.client)}</Typography>
          </Box>
          <Box display="flex" alignItems="center">
            <Typography fontWeight={600} width="25%">
              {tl("qrPayment.customerNumber")}
            </Typography>
            <Typography>{draft?.client?.customerNumber}</Typography>
          </Box>
          {draft?.client?.phone && (
            <Box display="flex" alignItems="center">
              <Typography width="25%">{tl("qrPayment.phone")}</Typography>
              <Typography>{draft.client.phone}</Typography>
            </Box>
          )}
          {draft?.client?.email && (
            <Box display="flex" alignItems="center">
              <Typography width="25%">{tl("qrPayment.email")}</Typography>
              <Typography>{draft.client.email}</Typography>
            </Box>
          )}
          {(draft?.client?.address || draft?.client?.city) && (
            <Box display="flex">
              <Typography width="25%">{tl("qrPayment.address")}</Typography>
              <Typography>
                {separateByComma(capitalize(draft.client.address), capitalize(draft.client.city))}
              </Typography>
            </Box>
          )}
        </Box>

        {/* bill items */}
        {draft?.billItems && draft?.billItems?.length > 0 && isProductSelected() && (
          <BillDraftItems draft={draft} />
        )}

        {/* qr  and summary */}
        <Box display="flex" justifyContent="space-between">
          <Box fontSize={scaleFont("0.3cm")} textTransform="uppercase">
            <Typography align="left" fontWeight={700} mb={1}>
              Scan To Pay with {selectedPaymentMethod}
            </Typography>

            {/* children */}
            {children}
          </Box>

          {/* bill summary */}
          <Box width="50%" fontSize={scaleFont("0.3cm")} textTransform="uppercase">
            <Box display="flex" flexDirection="column" gap="5px">
              <Box display="flex" gap="10px" alignItems="flex-start" justifyContent="space-between">
                <Typography>{tl("qrPayment.grossTotalAmount")}</Typography>
                <Typography>{rupeeDisplay(draft?.summary?.totalPerUnitTimesQty)}</Typography>
              </Box>

              {Boolean(draft?.summary?.discount) && (
                <Box
                  display="flex"
                  gap="10px"
                  alignItems="flex-start"
                  justifyContent="space-between"
                >
                  <Typography>
                    {draft?.settings?.discountSettings?.discountType === DiscountTypes.percentage
                      ? tl("qrPayment.discountPercentage")
                      : tl("qrPayment.discount")}{" "}
                  </Typography>
                  <Typography>
                    {draft?.settings?.discountSettings?.discountType === DiscountTypes.percentage
                      ? `${draft?.summary?.discountPct} %`
                      : rupeeDisplay(draft?.summary?.discount)}
                  </Typography>
                </Box>
              )}

              <Box display="flex" gap="10px" alignItems="flex-start" justifyContent="space-between">
                <Typography>{tl("qrPayment.taxableAmount")}</Typography>
                <Typography>{rupeeDisplay(draft?.summary?.taxableAmount)}</Typography>
              </Box>

              <Box display="flex" gap="10px" alignItems="flex-start" justifyContent="space-between">
                <Typography>{tl("qrPayment.totalAmount")}</Typography>
                <Typography>{rupeeDisplay(draft?.summary?.totalAmount)}</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Panel>
  );
}
